import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { ChevronDown, ChevronRight, Send, Users, Megaphone, MessageSquare, FileText, Settings, LayoutDashboard, Zap, Link2, Chrome, ShieldCheck } from "lucide-react";

function FAQItem({ question, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-100 last:border-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-start gap-3 py-4 text-left group"
      >
        <span className="mt-0.5 shrink-0 text-slate-400 group-hover:text-[#FF5C35] transition-colors">
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </span>
        <span className={`text-sm font-medium ${open ? "text-[#0F1419]" : "text-slate-700"}`}>
          {question}
        </span>
      </button>
      {open && (
        <div className="pb-4 pl-7 pr-4 text-sm text-slate-600 leading-relaxed space-y-2">
          {children}
        </div>
      )}
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="card p-6">
      <div className="flex items-center gap-2.5 mb-4 pb-3 border-b border-slate-100">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: "#FFF0EB" }}>
          <Icon size={16} style={{ color: "#FF5C35" }} />
        </div>
        <h3 className="font-semibold text-slate-900">{title}</h3>
      </div>
      {children}
    </div>
  );
}

export default function Help() {
  return (
    <div className="p-8 max-w-3xl">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold font-heading text-slate-900">Help & FAQ</h1>
        <p className="text-slate-500 text-sm mt-0.5">Everything you need to know about using Relay.</p>
      </div>

      <div className="space-y-5">

        {/* Getting Started */}
        <Section title="Getting Started" icon={LayoutDashboard}>
          <FAQItem question="What is Relay?">
            <p>Relay is an SMS marketing platform built for real estate teams that use FollowUp Boss. It lets you send mass text blasts, set up automated drip campaigns, manage two-way conversations, and track message delivery — all connected to your CRM contacts.</p>
          </FAQItem>
          <FAQItem question="How do I connect my FollowUp Boss account?">
            <p>Go to <strong>Settings</strong> and enter your FUB API key in the FollowUp Boss Integration section. You can find your API key in FUB under <strong>Admin → API Keys & Lead Email</strong>. Once entered, click <strong>Test Connection</strong> to verify, then <strong>Sync Contacts</strong> to pull in your leads.</p>
          </FAQItem>
          <FAQItem question="What is SignalWire and why do I need it?">
            <p>SignalWire is the SMS delivery provider that actually sends and receives your text messages. It's similar to Twilio but roughly 50% cheaper. You need a SignalWire account with a registered phone number to send real messages. Without it, the app runs in simulation mode (messages are logged but not delivered).</p>
          </FAQItem>
          <FAQItem question="What is 10DLC and do I need it?">
            <p>10DLC (10-Digit Long Code) is a carrier registration system required for sending business text messages in the US. You need to register your brand and campaign through your SignalWire dashboard using your business EIN. Without 10DLC registration, carriers may filter or block your messages. It's a one-time setup that takes 1–3 business days for approval.</p>
          </FAQItem>
        </Section>

        {/* Contacts */}
        <Section title="Contacts" icon={Users}>
          <FAQItem question="How do contacts get into the system?">
            <p>There are two ways: (1) <strong>Sync from FollowUp Boss</strong> — click Sync Contacts in Settings to pull all your FUB leads. This brings in their name, phone, email, stage, tags, and source. (2) <strong>Add manually</strong> — click the Add Contact button on the Contacts page to create a contact directly.</p>
          </FAQItem>
          <FAQItem question="What do the stages mean?">
            <p>Stages come directly from your FollowUp Boss pipeline. Common ones include Lead, Attempted Contact, Prospect, Active Client, and Past Client. You can filter by stage when sending blasts to target the right audience.</p>
          </FAQItem>
          <FAQItem question="What happens when someone opts out?">
            <p>When a contact replies "STOP" to any message, they are automatically marked as opted out. Opted-out contacts are excluded from all future blasts and campaigns. You can also manually toggle opt-out status from a contact's detail view. This is required for SMS compliance.</p>
          </FAQItem>
          <FAQItem question="How often should I sync contacts?">
            <p>Sync whenever you want to pull in new leads or updated information from FUB. There's no automatic sync — it runs on demand when you click the button. For active teams adding leads daily, syncing once a day or before a major blast is a good habit.</p>
          </FAQItem>
        </Section>

        {/* Mass Blast */}
        <Section title="Mass Blast" icon={Send}>
          <FAQItem question="What is a mass blast?">
            <p>A mass blast sends the same text message to multiple contacts at once. You select your audience (by stage, tags, source, or search), compose your message, and send. Each recipient gets an individual SMS — it's not a group text.</p>
          </FAQItem>
          <FAQItem question="What are merge fields?">
            <p>Merge fields are placeholders that get replaced with each contact's actual information when the message sends. Available merge fields:</p>
            <ul className="list-disc list-inside mt-1 space-y-0.5 text-slate-500">
              <li><code className="text-xs bg-slate-100 px-1 rounded">{"{first_name}"}</code> — Contact's first name</li>
              <li><code className="text-xs bg-slate-100 px-1 rounded">{"{last_name}"}</code> — Contact's last name</li>
              <li><code className="text-xs bg-slate-100 px-1 rounded">{"{agent_name}"}</code> — Your name (the logged-in agent)</li>
              <li><code className="text-xs bg-slate-100 px-1 rounded">{"{address}"}</code> — Property address if available</li>
            </ul>
          </FAQItem>
          <FAQItem question="Can I schedule a blast for later?">
            <p>Yes. On the Mass Blast page, toggle from "Send Now" to "Schedule" and pick a date and time. Scheduled blasts appear in the Scheduled section where you can cancel them before they send. The system checks every 60 seconds for blasts that are due.</p>
          </FAQItem>
          <FAQItem question="What's the difference between Mass Blast and Quick Blast?">
            <p><strong>Mass Blast</strong> is the full-page blast tool where you build your audience and compose your message from scratch. <strong>Quick Blast</strong> is a shortcut panel on the Contacts page — it lets you filter contacts by stage, tags, and source, then send a message directly without leaving the contacts view.</p>
          </FAQItem>
        </Section>

        {/* Campaigns */}
        <Section title="Campaigns" icon={Megaphone}>
          <FAQItem question="What is a campaign?">
            <p>A campaign is an automated drip sequence — a series of messages sent over time. You define multiple steps, each with a delay (in hours after enrollment). When you enroll contacts in a campaign, they automatically receive each message at the scheduled interval.</p>
          </FAQItem>
          <FAQItem question="How do I create a campaign?">
            <p>Go to <strong>Campaigns → New Campaign</strong>. Give it a name, set a trigger type (e.g., "New Lead"), then add your message steps with delays. Each step has a message body (supports merge fields) and a delay in hours from the previous step. Save when done, then enroll contacts.</p>
          </FAQItem>
          <FAQItem question="How do contacts get enrolled?">
            <p>Currently, you enroll contacts manually by selecting them and choosing a campaign. If you have a FUB webhook configured, new leads can be automatically enrolled in a designated campaign when they enter your CRM.</p>
          </FAQItem>
        </Section>

        {/* Inbox */}
        <Section title="Inbox" icon={MessageSquare}>
          <FAQItem question="How does the inbox work?">
            <p>The Inbox shows all SMS conversations organized by contact. Each thread displays the full message history — both outbound (messages you sent) and inbound (replies from the contact). Click any thread to view the conversation and send a reply.</p>
          </FAQItem>
          <FAQItem question="Why don't I see replies from contacts?">
            <p>Inbound messages require a webhook URL so SignalWire can forward replies to your app. This only works when the app is deployed to a public URL (not localhost). Once deployed, configure your SignalWire number's Message Request URL to point to your app's <code className="text-xs bg-slate-100 px-1 rounded">/api/signalwire/inbound</code> endpoint.</p>
          </FAQItem>
          <FAQItem question="What do the message status badges mean?">
            <ul className="space-y-1">
              <li><span className="inline-block px-2 py-0.5 text-xs rounded-full bg-amber-50 text-amber-700 border border-amber-200">queued</span> — Message accepted by SignalWire, waiting to deliver</li>
              <li><span className="inline-block px-2 py-0.5 text-xs rounded-full bg-green-50 text-green-700 border border-green-200">delivered</span> — Confirmed delivered to the recipient's phone</li>
              <li><span className="inline-block px-2 py-0.5 text-xs rounded-full bg-red-50 text-red-700 border border-red-200">failed</span> — Delivery failed (bad number, carrier blocked, etc.)</li>
              <li><span className="inline-block px-2 py-0.5 text-xs rounded-full bg-slate-100 text-slate-500 border border-slate-200">mock</span> — Sent in simulation mode (SignalWire not configured)</li>
            </ul>
          </FAQItem>
        </Section>

        {/* Templates */}
        <Section title="Templates" icon={FileText}>
          <FAQItem question="What are templates?">
            <p>Templates are pre-written messages you can save and reuse. Instead of typing the same message every time, save it as a template with a name and category. Templates support merge fields and are accessible from all compose areas in the app.</p>
          </FAQItem>
          <FAQItem question="How do I use a template when sending?">
            <p>When composing a blast or campaign message, click the template selector to browse your saved templates. Selecting one populates the message body, which you can still edit before sending.</p>
          </FAQItem>
        </Section>

        {/* FUB Widget */}
        <Section title="FUB Embedded Widget" icon={Link2}>
          <FAQItem question="What is the FUB widget?">
            <p>The embedded widget loads Relay directly inside the FollowUp Boss interface. When an agent views a lead in FUB, the widget appears in the sidebar showing the SMS conversation with that specific contact, plus the ability to send messages and enroll them in campaigns — without leaving FUB.</p>
          </FAQItem>
          <FAQItem question="How do I install the widget?">
            <p>In FollowUp Boss, go to <strong>Admin → Integrations → Embedded Apps</strong>. Create a new embedded app, paste your widget URL (found in Settings), name it "Relay", and enable it. It will appear on every lead profile.</p>
          </FAQItem>
          <FAQItem question="Does the widget also support mass blasts?">
            <p>Yes. The widget has a Mass Blast tab that lets you select contacts by stage, tags, or Smart Lists and send a blast directly from inside FUB.</p>
          </FAQItem>
        </Section>

        {/* Chrome Extension */}
        <Section title="Chrome Extension" icon={Chrome}>
          <FAQItem question="What is the Chrome extension?">
            <p>The Relay Chrome extension adds a mass blast button directly into the FollowUp Boss People page toolbar. Instead of switching to the Relay dashboard, you can filter contacts in FUB, click the SMS icon, and send a blast right from FUB's interface.</p>
          </FAQItem>
          <FAQItem question="How do I install the Chrome extension?">
            <p>Go to <strong>Settings → Chrome Extension for FUB</strong> and download the ZIP file. Unzip it to a permanent folder on your computer, then open <strong>chrome://extensions</strong> in Chrome, enable <strong>Developer mode</strong>, click <strong>"Load unpacked"</strong>, and select the unzipped folder. The SMS icon will appear in FUB's toolbar the next time you visit the People page.</p>
          </FAQItem>
          <FAQItem question="Do I need to log in separately in the extension?">
            <p>Yes, the first time you click the SMS icon in FUB, you'll be prompted to log in with your Relay credentials. Your session stays active for 6 days, so you won't need to log in again frequently.</p>
          </FAQItem>
          <FAQItem question="Can I use Smart Lists from FUB in the extension?">
            <p>Yes. The extension blast modal has two audience modes: <strong>Synced Contacts</strong> (filter by stage, tags, or search) and <strong>FUB Smart List</strong> (pick any of your saved Smart Lists). Both options support merge fields and the same blast features as the main app.</p>
          </FAQItem>
          <FAQItem question="The SMS icon isn't showing up in FUB. What should I do?">
            <p>Try these steps: (1) Make sure you're on the <strong>People</strong> page in FUB, not a contact detail page. (2) Check that the extension is enabled in <strong>chrome://extensions</strong>. (3) Refresh the FUB page — the extension waits for the toolbar to load. (4) If FUB updated their layout, the icon may need a re-inject — try refreshing again or reinstalling the latest version from Settings.</p>
          </FAQItem>
        </Section>

        {/* SignalWire & Technical */}
        <Section title="Technical & Troubleshooting" icon={Zap}>
          <FAQItem question="My messages show 'queued' but never update to 'delivered'. Why?">
            <p>Status updates require a webhook. SignalWire sends delivery confirmations to your app's <code className="text-xs bg-slate-100 px-1 rounded">/api/signalwire/status</code> endpoint. If your app is running on localhost, SignalWire can't reach it. Deploy to a public URL or use a tunnel (like ngrok) for testing.</p>
          </FAQItem>
          <FAQItem question="Messages are failing with a 'From invalid format' error.">
            <p>The phone number in your SignalWire settings must be in E.164 format: <code className="text-xs bg-slate-100 px-1 rounded">+1XXXXXXXXXX</code> (e.g., +17253537017). The app now auto-formats this for you — just enter or paste your number and it will be cleaned up automatically.</p>
          </FAQItem>
          <FAQItem question="What does 'simulation mode' mean?">
            <p>When SignalWire credentials aren't configured in Settings, the app runs in simulation mode. Messages are logged in the database but not actually sent. This is useful for testing the interface. Once you add your SignalWire credentials, messages are sent for real.</p>
          </FAQItem>
          <FAQItem question="Is there a limit to how many messages I can send?">
            <p>There's no limit built into Relay itself. Limits depend on your SignalWire account balance and your 10DLC campaign throughput. Standard 10DLC campaigns allow about 15 messages per second. For large blasts (1,000+ contacts), messages queue and send as fast as your throughput allows.</p>
          </FAQItem>
          <FAQItem question="How do I set up for production / go live?">
            <p>To go live you need: (1) Deploy the app to a hosting provider (Railway, Render, etc.) with a public URL. (2) Set the <code className="text-xs bg-slate-100 px-1 rounded">BASE_URL</code> environment variable to your production URL. (3) Configure your SignalWire number's webhook URLs to point to your production endpoints for inbound messages and delivery status. (4) Ensure your 10DLC campaign is approved.</p>
          </FAQItem>
        </Section>

        {/* Settings */}
        <Section title="Account & Settings" icon={Settings}>
          <FAQItem question="Can multiple agents use the same account?">
            <p>Currently each login sees all contacts. Multi-agent support (where each agent only sees their assigned contacts) is planned. For now, the onboarding pulls all agents from your FUB account so each person can identify themselves.</p>
          </FAQItem>
          <FAQItem question="How do I change my API keys?">
            <p>Go to Settings and enter the new key in the relevant field. For security, existing keys are shown as masked (••••). Entering a new value and clicking Save Settings will overwrite the old key.</p>
          </FAQItem>
        </Section>

        {/* Privacy */}
        <div className="card p-6">
          <div className="flex items-center gap-2.5 mb-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: "#FFF0EB" }}>
              <ShieldCheck size={16} style={{ color: "#FF5C35" }} />
            </div>
            <h3 className="font-semibold text-slate-900">Privacy & Legal</h3>
          </div>
          <p className="text-sm text-slate-600 mb-3">
            Learn how Relay collects, uses, and protects your data.
          </p>
          <NavLink
            to="/privacy"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[#FF5C35] hover:text-[#E84E29] transition-colors"
          >
            <ShieldCheck size={14} /> View Privacy Policy
          </NavLink>
        </div>

      </div>
    </div>
  );
}
